import React, { useState, useRef, ChangeEvent, FormEvent } from 'react';
import { batchUploadCards } from '../../services/cardAnalysisService';
import { BusinessCard } from '../../types';

interface BatchCardUploadProps {
  onUploadComplete: (cards: BusinessCard[]) => void;
  onError: (error: Error) => void;
}

const BatchCardUpload: React.FC<BatchCardUploadProps> = ({ onUploadComplete, onError }) => {
  const [titlePrefix, setTitlePrefix] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTitlePrefix(e.target.value);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(selectedFiles);
      setError(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!titlePrefix.trim()) {
      setError('Please enter a title prefix');
      return;
    }
    
    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadProgress(10);
      
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + 5;
          return newProgress < 90 ? newProgress : prev;
        });
      }, 300);
      
      const cards = await batchUploadCards(titlePrefix, files);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        setIsUploading(false);
        setFiles([]);
        setTitlePrefix('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onUploadComplete(cards);
      }, 500);
      
    } catch (err) {
      setIsUploading(false);
      setUploadProgress(0);
      setError('Error uploading files. Please try again.');
      onError(err as Error);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const imageFiles = droppedFiles.filter(file => file.type.startsWith('image/'));
      
      if (imageFiles.length === 0) {
        setError('Please drop image files only');
        return;
      }
      
      setFiles(imageFiles);
      setError(null);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Batch Upload Business Cards</h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title-prefix">
            Title Prefix
          </label>
          <input
            id="title-prefix"
            type="text"
            placeholder="Enter a title prefix for the batch"
            value={titlePrefix}
            onChange={handleTitleChange}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            disabled={isUploading}
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="file-upload">
            Business Card Images
          </label>
          <div
            className="border-2 border-dashed rounded-md p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </div>
            <div className="mt-2">
              <p className="text-sm font-medium">
                Drag and drop files, or click to select
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supports: JPG, PNG, GIF (max 10MB each)
              </p>
            </div>
            <input
              id="file-upload"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
              ref={fileInputRef}
              disabled={isUploading}
            />
          </div>
          
          {files.length > 0 && (
            <div className="mt-2">
              <p className="text-sm font-medium">{files.length} file(s) selected:</p>
              <ul className="text-xs text-gray-500 mt-1 max-h-24 overflow-y-auto">
                {files.map((file, index) => (
                  <li key={index} className="truncate">
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {isUploading && (
          <div className="mb-4">
            <div className="flex justify-between text-xs">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          </div>
        )}
        
        <button
          type="submit"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
          disabled={isUploading || files.length === 0 || !titlePrefix.trim()}
          onClick={handleSubmit}
        >
          {isUploading ? 'Uploading...' : 'Upload Batch'}
        </button>
      </form>
    </div>
  );
};

export default BatchCardUpload;
